import React, { useMemo } from 'react';
import useOrganizationGroups from '../../../../hooks/api/useOrganizationGroups';
import { SelectOption } from '../../../v2BaseComponents/Inputs/Select/types';
import { organizationRoles } from '../../../../utils/sharedMessages';
import { FormStack } from '../../../v2BaseComponents/Stack';
import { SplitFormRow } from '../../../v2BaseComponents/Form';
import { Field } from 'formik';
import { getIntl } from '../../../../utils/intl';
import { FormikInputs } from '../../../v2BaseComponents/Inputs';
import { required } from '../../../../utils/validators';
import { APITypes } from '../../../../primary-record-types';
import { UpdateOrganizationUserArgs } from '../../../../hooks/api/useOrganizationUsers';
import { useIsOrganizationOwner } from '../../../../hooks/api/useOrganization';

const intl = getIntl('edit-organization-user-fields', {
    role: 'Role',
    isBillingAdmin: 'Is Billing Admin?',
    groups: 'Groups',
});

export type FormValue = {
    isBillingAdmin: boolean;
    role: 'admin' | 'agent' | 'owner';
    groups: string[];
};

const ownerSelectOptions: SelectOption[] = [
    {
        value: 'owner',
        label: organizationRoles('owner'),
    },
];

const nonOwnerSelectOptions: SelectOption[] = [
    {
        value: 'admin',
        label: organizationRoles('admin'),
    },
    {
        value: 'agent',
        label: organizationRoles('agent'),
    },
];

export type EditOrganizationUserFieldsOwnProps = {
    initialValues: FormValue;
    values: FormValue;
};

export type EditOrganizationUserFieldsProps = EditOrganizationUserFieldsOwnProps;

const EditOrganizationUserFields: React.FC<EditOrganizationUserFieldsProps> = (props) => {
    const { groups, isLoadingGroups } = useOrganizationGroups({});
    const isOrganizationOwner = useIsOrganizationOwner();
    const selectOptions = useMemo(
        () => (props.initialValues.role === 'owner' ? ownerSelectOptions : nonOwnerSelectOptions),
        [props.initialValues.role],
    );
    const groupOptions = useMemo<SelectOption[]>(() => {
        return groups
            .map((group) => ({
                value: group.organizationGroupId,
                label: group.metadata.name,
                testID: group.metadata.name,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [groups]);

    return (
        <FormStack>
            <SplitFormRow>
                <Field
                    name="role"
                    title={intl('role')}
                    component={FormikInputs.Select}
                    isRequired
                    validate={required}
                    selectOptions={selectOptions}
                    isDisabled={props.initialValues.role === 'owner'}
                />
                {props.values.role === 'agent' ? (
                    <Field
                        name="groups"
                        title={intl('groups')}
                        component={FormikInputs.Select}
                        selectOptions={groupOptions}
                        isDisabled={isLoadingGroups || props.initialValues.role === 'owner'}
                        multiple
                    />
                ) : null}
            </SplitFormRow>
            {isOrganizationOwner && (
                <Field
                    name="isBillingAdmin"
                    title={intl('isBillingAdmin')}
                    component={FormikInputs.Checkbox}
                    isDisabled={props.initialValues.role === 'owner'}
                />
            )}
        </FormStack>
    );
};

export default React.memo(EditOrganizationUserFields);

export const getInitialValues = ({
    groups,
    user,
    userGroups,
}: {
    groups: APITypes.organizationService.ListOrganizationGroupsResponse['items'];
    user: APITypes.organizationService.OrganizationUserDetails | null;
    userGroups: APITypes.organizationService.ListOrganizationUserOrganizationGroupsResponse['items'];
}): FormValue => ({
    isBillingAdmin: !!user?.isBillingAdmin,
    role: user?.role || 'agent',
    groups: userGroups
        .map((group) => group.organizationGroupId)
        .filter((groupId) => groups.some((group) => group.organizationGroupId === groupId)),
});

export const formValuesToUpdateOrganizationUserArgs = ({
    organizationUserId,
    values,
    user,
    userGroups,
    isUpdatingUserOrganizationOwner,
}: {
    organizationUserId: string;
    values: FormValue;
    user: APITypes.organizationService.OrganizationUserDetails;
    userGroups: APITypes.organizationService.ListOrganizationUserOrganizationGroupsResponse['items'];
    isUpdatingUserOrganizationOwner: boolean;
}): UpdateOrganizationUserArgs => {
    const updateUserArgs: UpdateOrganizationUserArgs = {
        organizationUserId,
    };

    if (user!.role !== values.role) {
        updateUserArgs.updateRole = {
            role: values.role,
        };
    }

    // Only the organization owner can update the billing admin
    if (isUpdatingUserOrganizationOwner && user!.isBillingAdmin !== values.isBillingAdmin) {
        updateUserArgs.updateBillingAdmin = {
            isBillingAdmin: values.isBillingAdmin,
        };
    }

    if (
        values.role === 'agent' &&
        (userGroups.length !== values.groups.length ||
            !userGroups.every((group) => values.groups.includes(group.organizationGroupId)))
    ) {
        updateUserArgs.updateGroups = {
            organizationGroupIds: values.groups,
        };
    }

    return updateUserArgs;
};
